import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { extractPages, splitIntoIndividualPages, downloadPDF, getPageCount } from '../../utils/pdfUtils';
import { generateFilename } from '../../utils/fileUtils';
import JSZip from 'jszip';
import { downloadBlob } from '../../utils/fileUtils';
import './PDFSplitter.css';

const PDFSplitter = () => {
  const { currentFile, showNotification, setCurrentView } = useApp();
  const [selectedPages, setSelectedPages] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [isSplitting, setIsSplitting] = useState(false);
  const [splitMode, setSplitMode] = useState('extract'); // extract, individual

  useEffect(() => {
    if (currentFile && currentFile.pdfDoc) {
      setPageCount(getPageCount(currentFile.pdfDoc));
    }
  }, [currentFile]);

  if (!currentFile) {
    return (
      <div className="pdf-splitter-empty">
        <p>No PDF file loaded</p>
        <button className="btn btn-primary" onClick={() => setCurrentView('upload')}>
          Upload PDF
        </button>
      </div>
    );
  }

  const togglePage = (pageIndex) => {
    setSelectedPages(prev =>
      prev.includes(pageIndex)
        ? prev.filter(p => p !== pageIndex)
        : [...prev, pageIndex].sort((a, b) => a - b)
    );
  };

  const selectAll = () => {
    setSelectedPages(Array.from({ length: pageCount }, (_, i) => i));
  };

  const clearSelection = () => {
    setSelectedPages([]);
  };

  const handleExtractPages = async () => {
    if (selectedPages.length === 0) {
      showNotification('Please select at least one page', 'warning');
      return;
    }

    setIsSplitting(true);

    try {
      const newDoc = await extractPages(currentFile.pdfDoc, selectedPages);
      const filename = generateFilename(currentFile.name, 'extracted');
      await downloadPDF(newDoc, filename);
      showNotification(`Extracted ${selectedPages.length} pages successfully`, 'success');
    } catch (error) {
      console.error('Error extracting pages:', error);
      showNotification('Failed to extract pages', 'error');
    }

    setIsSplitting(false);
  };

  const handleSplitIntoIndividual = async () => {
    setIsSplitting(true);

    try {
      const individualDocs = await splitIntoIndividualPages(currentFile.pdfDoc);

      if (individualDocs.length === 1) {
        // Single page - just download it
        const filename = generateFilename(currentFile.name, 'page-1');
        await downloadPDF(individualDocs[0], filename);
      } else {
        // Multiple pages - create a ZIP file
        const zip = new JSZip();

        for (let i = 0; i < individualDocs.length; i++) {
          const pdfBytes = await individualDocs[i].save();
          const filename = generateFilename(currentFile.name, `page-${i + 1}`);
          zip.file(filename, pdfBytes);
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const zipFilename = generateFilename(currentFile.name, 'split').replace('.pdf', '.zip');
        downloadBlob(zipBlob, zipFilename);
      }

      showNotification(`Split into ${individualDocs.length} files successfully`, 'success');
    } catch (error) {
      console.error('Error splitting PDF:', error);
      showNotification('Failed to split PDF', 'error');
    }

    setIsSplitting(false);
  };

  const handleSplit = () => {
    if (splitMode === 'extract') {
      handleExtractPages();
    } else {
      handleSplitIntoIndividual();
    }
  };

  return (
    <div className="pdf-splitter-container">
      <div className="splitter-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('view')}>
          ← Back to Viewer
        </button>
        <h2>Split PDF</h2>
        <div className="split-mode">
          <label>
            <input
              type="radio"
              name="splitMode"
              value="extract"
              checked={splitMode === 'extract'}
              onChange={(e) => setSplitMode(e.target.value)}
            />
            Extract Selected Pages
          </label>
          <label>
            <input
              type="radio"
              name="splitMode"
              value="individual"
              checked={splitMode === 'individual'}
              onChange={(e) => setSplitMode(e.target.value)}
            />
            Split Into Individual Pages
          </label>
        </div>
      </div>

      {splitMode === 'extract' && (
        <div className="page-selection-controls">
          <div className="selection-info">
            <span>{selectedPages.length} of {pageCount} pages selected</span>
          </div>
          <div className="selection-actions">
            <button className="btn btn-secondary" onClick={selectAll}>
              Select All
            </button>
            <button className="btn btn-secondary" onClick={clearSelection}>
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="pages-grid">
        {Array.from({ length: pageCount }, (_, i) => i).map((pageIndex) => (
          <div
            key={pageIndex}
            className={`page-thumbnail ${
              splitMode === 'extract' && selectedPages.includes(pageIndex) ? 'selected' : ''
            } ${splitMode === 'individual' ? 'will-split' : ''}`}
            onClick={() => splitMode === 'extract' && togglePage(pageIndex)}
          >
            <div className="page-number">Page {pageIndex + 1}</div>
            {splitMode === 'extract' && selectedPages.includes(pageIndex) && (
              <div className="check-mark">✓</div>
            )}
          </div>
        ))}
      </div>

      <div className="splitter-actions">
        <button
          className="btn btn-success"
          onClick={handleSplit}
          disabled={isSplitting || (splitMode === 'extract' && selectedPages.length === 0)}
        >
          {isSplitting ? (
            <>
              <span className="spinner"></span>
              Processing...
            </>
          ) : splitMode === 'extract' ? (
            `Extract ${selectedPages.length} Page${selectedPages.length !== 1 ? 's' : ''}`
          ) : (
            `Split Into ${pageCount} Files`
          )}
        </button>
      </div>
    </div>
  );
};

export default PDFSplitter;
